import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ClockIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeSlashIcon,
  SparklesIcon,
  ChevronDownIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface Auction {
  id: string;
  title: string;
  image: string;
  currentBid: number;
  startingPrice: number;
  endTime: Date;
  auctionType: 'ENGLISH' | 'DUTCH' | 'VICKREY' | 'CANDLE';
  bidCount: number;
  creator: {
    name: string;
    avatar: string;
    verified: boolean;
  };
  collection: {
    name: string;
    floorPrice: number;
  };
  status: 'ACTIVE' | 'PENDING' | 'ENDING_SOON';
}

const auctionTypeConfig = {
  ENGLISH: {
    icon: ArrowTrendingUpIcon,
    label: 'English',
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-400',
  },
  DUTCH: {
    icon: ArrowTrendingDownIcon,
    label: 'Dutch',
    color: 'from-orange-500 to-red-600',
    bgColor: 'bg-orange-500/10',
    textColor: 'text-orange-400',
  },
  VICKREY: {
    icon: EyeSlashIcon,
    label: 'Vickrey',
    color: 'from-purple-500 to-violet-600',
    bgColor: 'bg-purple-500/10',
    textColor: 'text-purple-400',
  },
  CANDLE: {
    icon: FireIcon,
    label: 'Candle',
    color: 'from-amber-500 to-orange-600',
    bgColor: 'bg-amber-500/10',
    textColor: 'text-amber-400',
  },
};

// Mock data
const mockAuctions: Auction[] = [
  {
    id: '1',
    title: 'Equus Dualis #1',
    image: '/Equus Dualis/Equus Dualis II ) 1.png',
    currentBid: 12.5,
    startingPrice: 5,
    endTime: new Date(Date.now() + 3600000 * 2),
    auctionType: 'ENGLISH',
    bidCount: 23,
    creator: { name: 'ArtistX', avatar: '/warrior mythology/warrior mythology1.jpg', verified: true },
    collection: { name: 'Equus Dualis II', floorPrice: 8.2 },
    status: 'ACTIVE',
  },
  {
    id: '2',
    title: 'Warrior Mythology #14',
    image: '/warrior mythology/warrior mythology14.jpg',
    currentBid: 8.75,
    startingPrice: 15,
    endTime: new Date(Date.now() + 3600000 * 5),
    auctionType: 'DUTCH',
    bidCount: 0,
    creator: { name: 'CryptoMaster', avatar: '/warrior mythology/warrior mythology2.jpg', verified: true },
    collection: { name: 'Warrior Mythology', floorPrice: 6.5 },
    status: 'ACTIVE',
  },
  {
    id: '3',
    title: 'Equus Dualis #7',
    image: '/Equus Dualis/Equus Dualis II ) 7.png',
    currentBid: 0,
    startingPrice: 3,
    endTime: new Date(Date.now() + 3600000 * 24),
    auctionType: 'VICKREY',
    bidCount: 12,
    creator: { name: 'SecretArt', avatar: '/warrior mythology/warrior mythology3.jpg', verified: false },
    collection: { name: 'Equus Dualis II', floorPrice: 2.8 },
    status: 'ACTIVE',
  },
  {
    id: '4',
    title: 'Warrior Mythology #33',
    image: '/warrior mythology/warrior mythology33.jpg',
    currentBid: 45.2,
    startingPrice: 20,
    endTime: new Date(Date.now() + 1800000),
    auctionType: 'CANDLE',
    bidCount: 67,
    creator: { name: 'FireMaker', avatar: '/warrior mythology/warrior mythology4.jpg', verified: true },
    collection: { name: 'Warrior Mythology', floorPrice: 15.0 },
    status: 'ENDING_SOON',
  },
  {
    id: '5',
    title: 'Equus Dualis #15',
    image: '/Equus Dualis/Equus Dualis II ) 15.png',
    currentBid: 6.3,
    startingPrice: 4,
    endTime: new Date(Date.now() + 3600000 * 8),
    auctionType: 'ENGLISH',
    bidCount: 15,
    creator: { name: 'UrbanArt', avatar: '/warrior mythology/warrior mythology5.jpg', verified: true },
    collection: { name: 'Equus Dualis II', floorPrice: 5.1 },
    status: 'ACTIVE',
  },
  {
    id: '6',
    title: 'Warrior Mythology #22',
    image: '/warrior mythology/warrior mythology22.jpg',
    currentBid: 18.9,
    startingPrice: 25,
    endTime: new Date(Date.now() + 3600000 * 3),
    auctionType: 'DUTCH',
    bidCount: 0,
    creator: { name: 'MindBender', avatar: '/warrior mythology/warrior mythology6.jpg', verified: false },
    collection: { name: 'Warrior Mythology', floorPrice: 12.0 },
    status: 'ACTIVE',
  },
  {
    id: '7',
    title: 'Equus Dualis #19',
    image: '/Equus Dualis/Equus Dualis II ) 19.png',
    currentBid: 0,
    startingPrice: 10,
    endTime: new Date(Date.now() + 3600000 * 12),
    auctionType: 'VICKREY',
    bidCount: 45,
    creator: { name: 'TreasureHunt', avatar: '/warrior mythology/warrior mythology7.jpg', verified: true },
    collection: { name: 'Equus Dualis II', floorPrice: 8.5 },
    status: 'ACTIVE',
  },
  {
    id: '8',
    title: 'Warrior Mythology #66',
    image: '/warrior mythology/warrior mythology66.jpg',
    currentBid: 125.0,
    startingPrice: 50,
    endTime: new Date(Date.now() + 600000),
    auctionType: 'CANDLE',
    bidCount: 156,
    creator: { name: 'LegendaryArt', avatar: '/warrior mythology/warrior mythology8.jpg', verified: true },
    collection: { name: 'Warrior Mythology', floorPrice: 35.0 },
    status: 'ENDING_SOON',
  },
];

const AuctionsPage: React.FC = () => {
  const [auctions, setAuctions] = useState<Auction[]>(mockAuctions);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('ending_soon');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });

  const formatTimeLeft = (endTime: Date) => {
    const diff = endTime.getTime() - Date.now();
    if (diff <= 0) return 'Ended';

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m ${seconds}s`;
  };

  const toggleAuctionType = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const filteredAuctions = auctions.filter(auction => {
    if (searchQuery && !auction.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedTypes.length > 0 && !selectedTypes.includes(auction.auctionType)) {
      return false;
    }
    if (priceRange.min && auction.currentBid < parseFloat(priceRange.min)) {
      return false;
    }
    if (priceRange.max && auction.currentBid > parseFloat(priceRange.max)) {
      return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <div className="mb-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            <span className="text-gradient">Live Auctions</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-gray-400 text-lg"
          >
            Discover and bid on unique NFTs using our advanced auction mechanisms
          </motion.p>
        </div>

        {/* Auction Type Quick Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          {Object.entries(auctionTypeConfig).map(([type, config]) => {
            const Icon = config.icon;
            const isSelected = selectedTypes.includes(type);
            return (
              <button
                key={type}
                onClick={() => toggleAuctionType(type)}
                className={`relative p-4 rounded-xl border transition-all duration-300 ${
                  isSelected
                    ? `border-transparent bg-gradient-to-r ${config.color} shadow-lg shadow-${config.textColor}/20`
                    : 'border-white/10 bg-dark-800/50 hover:border-white/20'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-white/20' : config.bgColor}`}>
                    <Icon className={`h-5 w-5 ${isSelected ? 'text-white' : config.textColor}`} />
                  </div>
                  <div className="text-left">
                    <p className={`font-semibold ${isSelected ? 'text-white' : 'text-white'}`}>
                      {config.label}
                    </p>
                    <p className={`text-xs ${isSelected ? 'text-white/70' : 'text-gray-500'}`}>
                      {auctions.filter(a => a.auctionType === type).length} active
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </motion.div>

        {/* Search and Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          {/* Search */}
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search auctions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500 transition-colors"
            />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="appearance-none px-4 py-3 pr-10 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
            >
              <option value="ending_soon">Ending Soon</option>
              <option value="recently_listed">Recently Listed</option>
              <option value="price_low">Price: Low to High</option>
              <option value="price_high">Price: High to Low</option>
              <option value="most_bids">Most Bids</option>
            </select>
            <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-3 rounded-xl border transition-colors ${
              showFilters
                ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                : 'border-white/10 hover:border-white/20'
            }`}
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Filters</span>
          </button>

          {/* View Mode */}
          <div className="flex items-center space-x-1 bg-dark-800/50 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </motion.div>

        {/* Expanded Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 overflow-hidden"
            >
              <div className="p-6 bg-dark-800/50 border border-white/10 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Price Range */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Price Range (ETH)
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange.min}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                        className="w-full px-3 py-2 bg-dark-700 border border-white/10 rounded-lg focus:outline-none focus:border-primary-500"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange.max}
                        onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                        className="w-full px-3 py-2 bg-dark-700 border border-white/10 rounded-lg focus:outline-none focus:border-primary-500"
                      />
                    </div>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Status
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {['Active', 'Ending Soon', 'Pending'].map(status => (
                        <button
                          key={status}
                          className="px-3 py-1.5 bg-dark-700 border border-white/10 rounded-lg text-sm hover:border-primary-500 transition-colors"
                        >
                          {status}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Clear Filters */}
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setSelectedTypes([]);
                        setPriceRange({ min: '', max: '' });
                        setSearchQuery('');
                      }}
                      className="flex items-center space-x-2 px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                      <span>Clear All Filters</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-400">
            Showing <span className="text-white font-semibold">{filteredAuctions.length}</span> auctions
          </p>
          {selectedTypes.length > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-gray-500 text-sm">Active filters:</span>
              {selectedTypes.map(type => {
                const config = auctionTypeConfig[type as keyof typeof auctionTypeConfig];
                return (
                  <span
                    key={type}
                    className={`px-2 py-1 rounded-full text-xs ${config.bgColor} ${config.textColor}`}
                  >
                    {config.label}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Auctions Grid */}
        <motion.div
          layout
          className={viewMode === 'grid'
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'flex flex-col space-y-4'
          }
        >
          <AnimatePresence mode="popLayout">
            {filteredAuctions.map((auction, index) => {
              const typeConfig = auctionTypeConfig[auction.auctionType];
              const TypeIcon = typeConfig.icon;

              return viewMode === 'grid' ? (
                <motion.div
                  key={auction.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/auctions/${auction.id}`}
                    className="group block glass-card overflow-hidden hover:border-primary-500/50 transition-all duration-300"
                  >
                    {/* Image */}
                    <div className="relative aspect-square overflow-hidden">
                      <img
                        src={auction.image}
                        alt={auction.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />

                      {/* Auction Type Badge */}
                      <div className={`absolute top-3 left-3 flex items-center space-x-1.5 px-2.5 py-1 rounded-full ${typeConfig.bgColor} backdrop-blur-sm`}>
                        <TypeIcon className={`h-4 w-4 ${typeConfig.textColor}`} />
                        <span className={`text-xs font-medium ${typeConfig.textColor}`}>
                          {typeConfig.label}
                        </span>
                      </div>

                      {/* Time Badge */}
                      <div className={`absolute top-3 right-3 flex items-center space-x-1.5 px-2.5 py-1 rounded-full backdrop-blur-sm ${
                        auction.status === 'ENDING_SOON'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-dark-900/60 text-white'
                      }`}>
                        <ClockIcon className="h-4 w-4" />
                        <span className="text-xs font-medium">{formatTimeLeft(auction.endTime)}</span>
                      </div>

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      {/* Collection */}
                      <p className="text-xs text-gray-500 mb-1">{auction.collection.name}</p>

                      {/* Title */}
                      <h3 className="font-semibold text-white group-hover:text-primary-400 transition-colors mb-3 truncate">
                        {auction.title}
                      </h3>

                      {/* Creator */}
                      <div className="flex items-center space-x-2 mb-4">
                        <img
                          src={auction.creator.avatar}
                          alt={auction.creator.name}
                          className="w-6 h-6 rounded-full"
                        />
                        <span className="text-sm text-gray-400">
                          {auction.creator.name}
                        </span>
                        {auction.creator.verified && (
                          <SparklesIcon className="h-4 w-4 text-primary-400" />
                        )}
                      </div>

                      {/* Price */}
                      <div className="flex items-center justify-between pt-3 border-t border-white/5">
                        <div>
                          <p className="text-xs text-gray-500">
                            {auction.auctionType === 'DUTCH' ? 'Current Price' : 'Current Bid'}
                          </p>
                          <p className="text-lg font-bold text-white">
                            {auction.currentBid > 0 ? `${auction.currentBid} ETH` : 'No bids'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Bids</p>
                          <p className="text-lg font-semibold text-gray-300">{auction.bidCount}</p>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ) : (
                <motion.div
                  key={auction.id}
                  layout
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/auctions/${auction.id}`}
                    className="group flex items-center gap-6 p-4 glass-card hover:border-primary-500/50 transition-all duration-300"
                  >
                    {/* Image */}
                    <div className="relative w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img
                        src={auction.image}
                        alt={auction.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${typeConfig.bgColor} ${typeConfig.textColor}`}>
                          {typeConfig.label}
                        </span>
                        <span className="text-xs text-gray-500">{auction.collection.name}</span>
                      </div>
                      <h3 className="font-semibold text-white group-hover:text-primary-400 transition-colors truncate">
                        {auction.title}
                      </h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <img
                          src={auction.creator.avatar}
                          alt={auction.creator.name}
                          className="w-5 h-5 rounded-full"
                        />
                        <span className="text-sm text-gray-400">{auction.creator.name}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-8">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Current Bid</p>
                        <p className="font-bold text-white">
                          {auction.currentBid > 0 ? `${auction.currentBid} ETH` : 'No bids'}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Bids</p>
                        <p className="font-semibold text-gray-300">{auction.bidCount}</p>
                      </div>
                      <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full ${
                        auction.status === 'ENDING_SOON'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-dark-700 text-white'
                      }`}>
                        <ClockIcon className="h-4 w-4" />
                        <span className="text-sm font-medium">{formatTimeLeft(auction.endTime)}</span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {/* Empty State */}
        {filteredAuctions.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-dark-800 flex items-center justify-center">
              <MagnifyingGlassIcon className="h-10 w-10 text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold mb-2">No auctions found</h3>
            <p className="text-gray-400 mb-6">Try adjusting your filters or search query</p>
            <button
              onClick={() => {
                setSelectedTypes([]);
                setPriceRange({ min: '', max: '' });
                setSearchQuery('');
              }}
              className="btn-primary"
            >
              Clear Filters
            </button>
          </motion.div>
        )}

        {/* Load More */}
        {filteredAuctions.length > 0 && (
          <div className="text-center mt-12">
            <button className="btn-secondary">
              Load More Auctions
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuctionsPage;
